import type { JSX } from 'react';
import type { CuratorIgPostDetailData } from '@/services/slaydAPI/posts';
import type { Product } from '@/types/Product.types';
import styles from '../ApprovalPortalPage.module.css';
import { ApprovalActions } from './ApprovalActions';
import { CuratorCard } from './CuratorCard';
import { PostMedia } from './PostMedia';
import { ProductsGrid } from './ProductsGrid';

interface PostDetailProps {
  loadingDetail: boolean;
  selectedPost: CuratorIgPostDetailData | null;
  handleApprove: (approved: boolean) => void;
  approvalStatus: 'idle' | 'approving' | 'success' | 'error';
  products: Product[];
}

export const PostDetail = ({
  loadingDetail,
  selectedPost,
  handleApprove,
  approvalStatus,
  products,
}: PostDetailProps): JSX.Element => {
  if (loadingDetail) {
    return (
      <div className={styles.reviewPane}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  if (!selectedPost) {
    return (
      <div className={styles.reviewPane}>
        <div className={styles.emptyState}>Select a post to review</div>
      </div>
    );
  }

  return (
    <div className={styles.reviewPane}>
      {/* Sticky header: curator identity + approve/reject */}
      <div className={styles.reviewHeader}>
        {selectedPost.curator ? (
          <CuratorCard
            curator={selectedPost.curator}
            postTypeDisplay={selectedPost.postTypeDisplay}
          />
        ) : (
          <span className={styles.typeBadge}>
            {selectedPost.postTypeDisplay}
          </span>
        )}
        <ApprovalActions
          isApproved={selectedPost.isApproved}
          approvalStatus={approvalStatus}
          onApprove={handleApprove}
        />
      </div>

      {/* Two-column body */}
      <div className={styles.reviewBody}>
        <div className={styles.reviewBodyLeft}>
          {selectedPost.caption && (
            <div className={styles.card}>
              <p className={styles.captionText}>{selectedPost.caption}</p>
            </div>
          )}

          <PostMedia
            postType={selectedPost.postType}
            mediaImages={selectedPost.mediaImages || []}
            mediaVideo={selectedPost.mediaVideo}
          />

          <div className={styles.metaStrip}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Posted</span>
              <span className={styles.metaValue}>
                {selectedPost.postedAt
                  ? new Date(selectedPost.postedAt).toLocaleDateString(
                      'en-US',
                      {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      }
                    )
                  : '—'}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Curation</span>
              <span className={styles.metaValue}>
                {selectedPost.curation?.title || '—'}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Post ID</span>
              <span className={styles.metaValue}>{selectedPost.id}</span>
            </div>
          </div>
        </div>

        <div className={styles.reviewBodyRight}>
          <ProductsGrid products={products} />
        </div>
      </div>
    </div>
  );
};
